import { ClassValue } from 'clsx';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useBoolean } from '@shared/util/hooks/useBoolean';
import { i18n } from '@shared/util/i18n';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import { Dialog, DialogProps } from './Dialog';
import { useFitLongestLine } from './useFitLongestLine';
import styles from './ModalAlert.module.css';

export interface ConfirmProps extends Omit<DialogProps, 'title' | 'mode'> {
  onClose?: () => void | Promise<void>;
  title?: ReactNode;
  message?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm?: () => void | Promise<void>;
  cancelLabel?: string;
  onCancel?: () => void | Promise<void>;
  /**
   * Custom footer buttons. When provided, the default cancel/confirm buttons are
   * not rendered — the caller supplies the buttons and manages their own click
   * handlers (including dismissing via `onClose`/`onOpenChange`).
   */
  buttons?: ReactNode;
  inline?: boolean;
  className?: ClassValue;
}

export function Confirm({ inline = false, ...props }: ConfirmProps) {
  if (inline) {
    return <ConfirmContent {...props} />;
  } else {
    return <ModalConfirm {...props} />;
  }
}

function ModalConfirm({
  title,
  message,
  children,
  confirmLabel,
  danger,
  onConfirm,
  cancelLabel,
  onCancel,
  buttons,
  isOpen = true,
  onClose,
  onOpenChange,
  className,
  lightDismiss,
  testID = 'confirm-dialog',
}: ConfirmProps) {
  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        void onClose?.();
      }
    },
    [onClose, onOpenChange],
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  return (
    <Dialog
      mode="modal"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      lightDismiss={lightDismiss}
      testID={testID}
      className={[styles.alert, className]}
      animate
    >
      <ConfirmContent
        title={title}
        message={message}
        confirmLabel={confirmLabel}
        danger={danger}
        onConfirm={onConfirm}
        cancelLabel={cancelLabel}
        onCancel={onCancel}
        buttons={buttons}
        onClose={handleClose}
      >
        {children}
      </ConfirmContent>
    </Dialog>
  );
}

function ConfirmContent({
  title,
  message,
  children = message,
  confirmLabel = i18n('common.confirm'),
  danger = false,
  onConfirm,
  cancelLabel = i18n('common.cancel'),
  onCancel,
  buttons,
  onClose,
}: ConfirmProps & { onClose?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [loading, startLoading, stopLoading] = useBoolean();
  const messageRef = useRef<HTMLDivElement>(null);
  useFitLongestLine(messageRef, children);

  const handleConfirm = useCallback(async () => {
    startLoading();
    try {
      await onConfirm?.();
      onClose?.();
    } finally {
      stopLoading();
    }
  }, [onClose, onConfirm, startLoading, stopLoading]);

  useEffect(() => {
    // auto-focus primary button if not dangerous, else focus cancel button
    const element = ref.current;
    if (element) {
      const { signal, abort } = getController();
      setControlledTimeout(
        () => {
          const primaryButton = element.querySelector<HTMLButtonElement>(
            'button[data-variant="primary"]:not([data-danger])',
          );
          const cancelButton = element.querySelector<HTMLButtonElement>(
            'button:not([data-danger])',
          );
          const firstBtn = primaryButton ?? cancelButton;
          firstBtn?.focus();
        },
        10,
        signal,
      );

      return abort;
    }
  }, []);

  return (
    <View gap={3} className={styles.confirmContent} ref={ref}>
      <View gap={1}>
        {title && (
          <Text
            size="h4"
            weight="semibold"
            numberOfLines={3}
            align="center"
            balance
            className={styles.title}
          >
            {title}
          </Text>
        )}
        <View ref={messageRef} className={styles.message}>
          <Text size="body1" align="left" pretty>
            {children}
          </Text>
        </View>
      </View>
      {buttons}
      {!buttons && (
        <View row gap={1} center>
          <Button
            variant="ghost"
            size="large"
            label={cancelLabel}
            disabled={loading}
            testID="confirm-dialog-cancel-button"
            onClick={async () => {
              await onCancel?.();
              onClose?.();
            }}
          />
          <Button
            variant="primary"
            size="large"
            label={confirmLabel}
            testID="confirm-dialog-confirm-button"
            danger={danger}
            onClick={handleConfirm}
          />
        </View>
      )}
    </View>
  );
}
